import React from 'react';

interface EvidenceContinuumDiagramProps {
  showLabels?: boolean;
  interactive?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const EvidenceContinuumDiagram: React.FC<EvidenceContinuumDiagramProps> = ({
  showLabels = true,
  size = 'large'
}) => {
  const heights = {
    small: 80,
    medium: 120,
    large: 160
  };
  
  const height = heights[size];
  const width = size === 'small' ? 400 : size === 'medium' ? 600 : 800;

  return (
    <div className="w-full max-w-4xl mx-auto">
      {showLabels && (
        <div className="flex justify-between items-center mb-2 px-4">
          <h3 className="text-lg font-bold text-charcoal-900">Evidence of Success Range</h3>
          <p className="text-sm text-charcoal-600">
            From minimally acceptable to ideally perfect
          </p>
        </div>
      )}
      
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        role="img"
        aria-label="Evidence continuum from Barely Acceptable through Targeted Success to Hopelessly Idealistic"
      >
        {/* Background gradient */}
        <defs>
          <linearGradient id="continuum-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fecaca" />
            <stop offset="50%" stopColor="#93c5fd" />
            <stop offset="100%" stopColor="#86efac" />
          </linearGradient>
          
          <linearGradient id="arrow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
          
          <filter id="shadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.2" />
          </filter>
        </defs>
        
        {/* Main arrow body */}
        <g filter="url(#shadow)">
          <rect
            x="40"
            y={height / 2 - 20}
            width={width - 140}
            height="40"
            fill="url(#arrow-gradient)"
            rx="4"
          />
          
          {/* Arrow head */}
          <polygon
            points={`${width - 100},${height / 2 - 35} ${width - 40},${height / 2} ${width - 100},${height / 2 + 35}`}
            fill="url(#arrow-gradient)"
          />
        </g>
        
        {/* Section dividers */}
        <line
          x1={width / 3 + 10}
          y1={height / 2 - 25}
          x2={width / 3 + 10}
          y2={height / 2 + 25}
          stroke="white"
          strokeWidth="2"
          opacity="0.7"
        />
        <line
          x1={2 * width / 3 - 20}
          y1={height / 2 - 25}
          x2={2 * width / 3 - 20}
          y2={height / 2 + 25}
          stroke="white"
          strokeWidth="2"
          opacity="0.7"
        />
        
        {/* Labels on arrow */}
        <text
          x="40"
          y={height / 2 - 40}
          fill="#dc2626"
          fontSize={size === 'small' ? 12 : size === 'medium' ? 14 : 16}
          fontWeight="bold"
          textAnchor="start"
        >
          Criteria for Sufficiency
        </text>
        
        <text
          x={width / 2 - 40}
          y={height / 2 - 40}
          fill="#3b82f6"
          fontSize={size === 'small' ? 12 : size === 'medium' ? 14 : 16}
          fontWeight="bold"
          textAnchor="middle"
        >
          Criteria for Success
        </text>
        
        <text
          x={width - 100}
          y={height / 2 - 40}
          fill="#10b981"
          fontSize={size === 'small' ? 12 : size === 'medium' ? 14 : 16}
          fontWeight="bold"
          textAnchor="end"
        >
          Criteria for Success
        </text>
        
        {/* Section labels below */}
        <text
          x={width / 6 + 20}
          y={height / 2 + 55}
          fill="#991b1b"
          fontSize={size === 'small' ? 11 : size === 'medium' ? 13 : 15}
          fontWeight="600"
          textAnchor="middle"
        >
          Barely Acceptable
        </text>
        
        <text
          x={width / 2 - 40}
          y={height / 2 + 55}
          fill="#1e40af"
          fontSize={size === 'small' ? 11 : size === 'medium' ? 13 : 15}
          fontWeight="600"
          textAnchor="middle"
        >
          Criteria for Targeted Success
        </text>
        
        <text
          x={5 * width / 6 - 50}
          y={height / 2 + 55}
          fill="#047857"
          fontSize={size === 'small' ? 11 : size === 'medium' ? 13 : 15}
          fontWeight="600"
          textAnchor="middle"
        >
          Hopelessly Idealistic
        </text>
        
        {/* Threshold markers */}
        {size !== 'small' && (
          <>
            <circle cx={width / 3 + 10} cy={height / 2} r="6" fill="white" stroke="#3b82f6" strokeWidth="2" />
            <circle cx={2 * width / 3 - 20} cy={height / 2} r="6" fill="white" stroke="#10b981" strokeWidth="2" />
            
            {/* Threshold labels */}
            <text
              x={width / 3 + 10}
              y={height / 2 + 75}
              fill="#6b7280"
              fontSize="10"
              fontWeight="500"
              textAnchor="middle"
            >
              Threshold 1
            </text>
            <text
              x={2 * width / 3 - 20}
              y={height / 2 + 75}
              fill="#6b7280"
              fontSize="10"
              fontWeight="500"
              textAnchor="middle"
            >
              Threshold 2
            </text>
          </>
        )}
      </svg>
      
      {showLabels && size === 'large' && (
        <div className="mt-6 grid grid-cols-3 gap-4 px-4">
          <div className="bg-[var(--coral-dim)] border-2 border-[var(--coral)] p-3">
            <h4 className="font-bold text-[var(--coral)] text-sm mb-1">Barely Acceptable</h4>
            <p className="text-xs text-[var(--coral)]">
              Minimally sufficient—truly good enough to ship at expected delivery time
            </p>
          </div>
          <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-3">
            <h4 className="font-bold text-[var(--text-sec)] text-sm mb-1">Targeted Success</h4>
            <p className="text-xs text-[var(--text-sec)]">
              What we aim to hit—ambitious yet plausible with planned resources
            </p>
          </div>
          <div className="bg-[var(--green-dim)] border-2 border-[var(--green)] p-3">
            <h4 className="font-bold text-[var(--green)] dark:text-[var(--text-ter)] text-sm mb-1">Hopelessly Idealistic</h4>
            <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
              Stretch beyond current constraints—doesn't need to be possible today
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

